// <!-- glow-menopause-style-enhancements -->
import React, { useState } from 'react';
import { ArrowRight, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import ConditionExitPopup from '../components/ConditionExitPopup';
import TrustStrip from '../components/TrustStrip';
import ClinicianCard from '../components/ClinicianCard';
import ServicesDropdown from '../components/ServicesDropdown';

const PMDDLanding = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const scrollToBooking = () => {
    window.location.href = '/book';
  };

  const painPoints = [
    {
      title: 'Rage that scares the people you love',
      pain: 'Are you screaming at your partner over something small, then sobbing in the bathroom an hour later wondering who you have become?',
      description: 'PMDD is not a personality flaw. It is a severe neurochemical reaction to your own hormones, and it can absolutely be treated.'
    },
    {
      title: 'Darkness that has no explanation',
      pain: 'Do you spend a week or two every month feeling hopeless, worthless, or wondering if your family would actually be better off without you?',
      description: 'Cyclical suicidal ideation is one of the most under-recognized symptoms of PMDD. It is not weakness. It is hormonal, and it deserves real care, not dismissal.'
    },
    {
      title: 'Two completely different versions of you',
      pain: 'Are you fine, capable, and happy two weeks of the month, then unrecognizable, anxious, and overwhelmed the other two?',
      description: 'PMDD only shows up in the luteal phase. That stark before-and-after pattern is not "just PMS." It is a clinical hormonal disorder.'
    },
    {
      title: 'Relationships you keep almost destroying',
      pain: 'Are you watching your marriage, friendships, or career take real damage every single month and feeling powerless to stop it?',
      description: 'PMDD does not just hurt you. It strains everything around you. The shame loop of "I did it again" makes it even worse, and you are not the problem.'
    },
    {
      title: 'Doctors who told you it is "just stress"',
      pain: 'Have you been handed an antidepressant, told to do yoga, or told that "all women have PMS", when you know what you are experiencing is something else entirely?',
      description: 'PMDD is wildly under-diagnosed. Most providers were never trained to recognize it. You are not exaggerating. You need someone who actually understands cyclical hormone disorders.'
    },
    {
      title: 'The exhaustion of bracing for yourself',
      pain: 'Do you live month to month tracking calendars, warning your partner, canceling plans during "that week," and never feeling truly safe in your own body?',
      description: 'Living in a constant state of "when is the next crash coming" is its own trauma. You deserve a life where you are not at war with your own cycle.'
    }
  ];

  const emotionalImpact = [
    {
      area: 'Your marriage',
      impact: 'Fights you cannot take back, intimacy you cannot reach, a partner who is walking on eggshells'
    },
    {
      area: 'Your career',
      impact: 'Calling out sick during luteal week, missing deadlines, second-guessing every email you send'
    },
    {
      area: 'Your motherhood',
      impact: 'Snapping at your kids, then crying yourself to sleep with guilt for being the mother you swore you would never be'
    },
    {
      area: 'Your sense of self',
      impact: 'Never knowing which version of you will show up tomorrow, and grieving the woman you are when you are well'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'Tell us what is really happening',
      description: 'Share the cycle, the symptoms, the patterns, and the things you have only ever told your closest people. We listen without judgment.'
    },
    {
      number: '2',
      title: 'Get a real PMDD evaluation',
      description: 'Cycle tracking, hormone panels, and a clinical assessment to confirm whether what you are experiencing is PMDD or something else entirely.'
    },
    {
      number: '3',
      title: 'Receive a personalized plan',
      description: 'Targeted hormone support, evidence-based medication options when appropriate, and lifestyle protocols built around YOUR luteal phase, not a textbook.'
    },
    {
      number: '4',
      title: 'Start getting your months back',
      description: 'Ongoing care and adjustments so the gap between "the good you" and "the luteal you" finally starts to close.'
    }
  ];

  const proofPoints = [
    {
      stat: '1 in 20',
      title: 'Women have PMDD',
      description: 'Roughly 5-8% of menstruating women meet the clinical criteria for PMDD. You are not rare. You are just rarely listened to.'
    },
    {
      stat: '12 yrs',
      title: 'Average time to diagnosis',
      description: 'Most women with PMDD are misdiagnosed for over a decade before getting the right answer. That is twelve years too long.'
    },
    {
      stat: '70%+',
      title: 'See meaningful improvement',
      description: 'With the right treatment plan, the majority of women with PMDD report dramatic relief, not just "manageable," but actually better.'
    }
  ];

  const faqs = [
    {
      question: 'How is PMDD different from PMS?',
      answer: 'PMS causes mild to moderate physical and emotional symptoms before your period. PMDD is a severe, disabling form of premenstrual disorder that disrupts your relationships, work, and mental health for 1-2 weeks every cycle. It is recognized in the DSM-5 as a distinct mental health condition, not just "bad PMS."'
    },
    {
      question: 'My doctor said I just have anxiety or depression. How do I know if it is actually PMDD?',
      answer: 'The hallmark of PMDD is the cyclical pattern. Symptoms appear after ovulation, peak in the days before your period, and lift within a few days of bleeding starting. Tracking your symptoms across two cycles is one of the most powerful diagnostic tools, and we use it as a foundation.'
    },
    {
      question: 'What treatments actually work for PMDD?',
      answer: 'Effective PMDD treatment is highly individualized and may include hormone optimization, targeted SSRIs (often used cyclically), specific micronutrient protocols, lifestyle interventions, and in some cases continuous hormonal therapy. We build the plan around your specific symptom pattern.'
    },
    {
      question: 'Will I have to be on medication forever?',
      answer: 'Not necessarily. Some women use cyclical or low-dose medication during their highest-risk window. Others find that hormone optimization, nutrient repletion, and lifestyle work is enough. The goal is the lowest effective intervention that keeps you well.'
    },
    {
      question: 'I have had thoughts of self-harm during my luteal phase. Is that actually PMDD?',
      answer: 'Cyclical suicidal ideation that resolves once your period starts is a recognized symptom of severe PMDD, and it is medically urgent. You deserve real, fast care. If you are in crisis right now, please call or text 988. Then book a consultation so we can build a plan to keep you safe long-term.'
    }
  ];

  return (
    <div className="pcos-page">
      {/* Header */}
      <header className="pcos-header">
        <div className="pcos-container pcos-header-inner">
          <a href="/" className="pcos-brand" data-testid="pmdd-brand-link">
            <img
              src="https://customer-assets.emergentagent.com/job_hormone-wellness-1/artifacts/emqp5y2s_symbol%20%281%29.svg"
              alt="Glow Health Logo"
              className="brand-symbol"
            />
            <span>Glow Health</span>
          </a>
          <div className="pcos-header-actions">
            <ServicesDropdown testIdPrefix="pmdd-nav-services" />
            <button className="pcos-btn pcos-btn-primary" onClick={scrollToBooking} data-testid="pmdd-header-cta">
              Book Your Free Consultation
            </button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="pcos-hero">
          <div className="pcos-container pcos-hero-split">
            <div className="pcos-hero-content">
              <span className="pcos-eyebrow">PMDD Support</span>
              <h1 className="pcos-hero-headline">
                You are not "too much."<br />
                <span className="pcos-highlight">You have PMDD, and you deserve real help.</span>
              </h1>
              <p className="pcos-hero-sub">
                If you have spent years being told it is "just PMS," if you live in fear of the next luteal crash, if you are tired of apologizing for a version of yourself you cannot control, you are not broken. You have a hormonal disorder that is treatable. Most providers just are not trained to see it.
              </p>
              <div className="pcos-hero-actions">
                <button className="pcos-btn pcos-btn-primary pcos-btn-large" onClick={scrollToBooking} data-testid="pmdd-hero-cta">
                  Get Real Answers
                  <ArrowRight size={18} />
                </button>
                <a href="#pain" className="pcos-btn pcos-btn-secondary" data-testid="pmdd-hero-secondary-cta">See If This Sounds Like You</a>
              </div>
              <p className="pcos-hero-note">Free consultation. No judgment. Finally, someone who gets it.</p>
              <div className="meno-hero-credentials" data-testid="pmdd-hero-credentials">
                <p><strong>Utah-licensed clinician</strong> experienced in PMDD care.</p>
                <p>Serving women in Utah via secure HIPAA video visits.</p>
              </div>
            </div>
            <div className="pcos-hero-image">
              <img
                src="https://images.unsplash.com/photo-1502323936172-532616ad0c5b?w=640&h=760&fit=crop&auto=format"
                alt="A woman who finally got the care she needed"
                loading="lazy"
              />
              <div className="pcos-hero-image-badge">
                "I have my cycles back."
              </div>
            </div>
          </div>
        </section>

        <TrustStrip />

        {/* Three-panel journey strip */}
        <section className="meno-journey" data-testid="pmdd-journey">
          <div className="pcos-container">
            <div className="meno-journey-grid">
              <figure className="meno-journey-panel" data-testid="pmdd-journey-panel-1">
                <div className="meno-journey-image">
                  <img src="/pmdd-panel-1.png" alt="A woman curled on a sofa, enduring a hard cycle week with quiet dignity" loading="lazy" />
                </div>
                <figcaption>You are not crazy. PMDD is real.</figcaption>
              </figure>
              <figure className="meno-journey-panel" data-testid="pmdd-journey-panel-2">
                <div className="meno-journey-image">
                  <img src="/pmdd-panel-2.png" alt="A woman on a warm telehealth video visit with a clinician who treats cycle-based mood seriously" loading="lazy" />
                </div>
                <figcaption>Talk with a clinician who takes cycle-based mood seriously.</figcaption>
              </figure>
              <figure className="meno-journey-panel" data-testid="pmdd-journey-panel-3">
                <div className="meno-journey-image">
                  <img src="/pmdd-panel-3.png" alt="A woman walking peacefully with a journal, stable and grounded" loading="lazy" />
                </div>
                <figcaption>A plan that protects the version of you that you actually are.</figcaption>
              </figure>
            </div>
          </div>
        </section>

        {/* Specialist value prop */}
        <section className="meno-specialist" data-testid="pmdd-specialist">
          <div className="pcos-container">
            <p><strong>Not a general telehealth clinic.</strong> We specialize in PMDD, so your symptoms are not minimized as &quot;just PMS&quot; or &quot;just anxiety.&quot;</p>
          </div>
        </section>


        {/* Pain Points */}
        <section className="pcos-section pcos-pain-section" id="pain">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">Does This Sound Like You?</span>
              <h2>The symptoms you are too ashamed to say out loud</h2>
              <p>PMDD is not "moody." It is a clinical hormonal disorder that hijacks 1-2 weeks of your life every single cycle. The shame is part of the disease. So is the relief when someone finally names it.</p>
            </div>
            <div className="pcos-pain-grid">
              {painPoints.map((point, index) => (
                <div key={index} className="pcos-pain-card" data-testid={`pmdd-pain-card-${index}`}>
                  <h3>{point.title}</h3>
                  <p className="pcos-pain-question">{point.pain}</p>
                  <p className="pcos-pain-explain">{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Emotional Impact */}
        <section className="pcos-section pcos-impact-section">
          <div className="pcos-container">
            <div className="pcos-impact-wrap">
              <div className="pcos-impact-content">
                <span className="pcos-eyebrow">The Hidden Cost</span>
                <h2>PMDD steals more than your good days. It steals your sense of who you are.</h2>
                <p className="pcos-impact-intro">
                  The hormones reset every month. The damage doesn't.
                </p>
              </div>
              <div className="pcos-impact-grid">
                {emotionalImpact.map((item, index) => (
                  <div key={index} className="pcos-impact-card" data-testid={`pmdd-impact-card-${index}`}>
                    <h4>{item.area}</h4>
                    <p>{item.impact}</p>
                  </div>
                ))}
              </div>
              <p className="pcos-impact-cta">
                <strong>You are not destroying your life. PMDD is. And it is treatable.</strong>
              </p>
            </div>
          </div>
        </section>

        {/* Quote */}
        <section className="pcos-section pcos-quote-section" style={{backgroundImage: "url('/quote-bg-pmdd.png')"}}>
          <div className="pcos-container">
            <div className="pcos-quote-wrap">
              <blockquote>
                <p>"I thought I was a bad wife and a worse mother. I had no idea two weeks of every month I was actually unwell. The diagnosis alone was healing. The treatment gave me my life back."</p>
                <cite>Real patient story</cite>
              </blockquote>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="pcos-section" id="plan">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">How We Help</span>
              <h2>A real approach to PMDD care</h2>
              <p>No more "everyone has PMS." No more being handed a generic SSRI and sent home. No more figuring this out alone.</p>
            </div>
            <div className="pcos-steps-grid">
              {steps.map((step, index) => (
                <div key={index} className="pcos-step-card" data-testid={`pmdd-step-card-${index}`}>
                  <div className="pcos-step-number">{step.number}</div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              ))}
            </div>
            <div className="pcos-steps-cta">
              <button className="pcos-btn pcos-btn-primary pcos-btn-large" onClick={scrollToBooking} data-testid="pmdd-steps-cta">
                Book Your Free Consultation
                <ArrowRight size={18} />
              </button>
              <p className="pcos-cta-note">No commitment. No pressure. Just answers.</p>
            </div>
          </div>
        </section>

        {/* Proof Points */}
        <section className="pcos-section pcos-proof-section">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">The Reality</span>
              <h2>You are not alone, even though it feels that way</h2>
            </div>
            <div className="pcos-proof-grid">
              {proofPoints.map((point, index) => (
                <div key={index} className="pcos-proof-card" data-testid={`pmdd-proof-card-${index}`}>
                  <strong>{point.stat}</strong>
                  <h3>{point.title}</h3>
                  <p>{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ClinicianCard />

        {/* Who this is right for */}
        <section className="pcos-section meno-fit-section" data-testid="pmdd-fit-section">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">A Quick Honest Check</span>
              <h2>Who My Glow Health is right for</h2>
              <p>We are not for everyone, and that is the point. Here is who tends to feel the biggest shift working with us.</p>
            </div>
            <div className="meno-fit-grid">
              <div className="meno-fit-card meno-fit-yes" data-testid="pmdd-fit-yes">
                <h3>This is right for you if</h3>
                <ul>
                  <li>You live in Utah and want secure telehealth visits.</li>
                  <li>Your worst days follow a cycle pattern, not a random one.</li>
                  <li>You want a clinician who treats PMDD as the real condition it is.</li>
                  <li>You are open to evidence-based options including hormone and mood support.</li>
                  <li>You want a plan that fits the version of you that you actually are.</li>
                </ul>
              </div>
              <div className="meno-fit-card meno-fit-no" data-testid="pmdd-fit-no">
                <h3>This is probably not for you if</h3>
                <ul>
                  <li>Your symptoms are not cyclical and do not relate to your menstrual cycle.</li>
                  <li>You want a quick prescription with no underlying workup.</li>
                  <li>You are not interested in hormone-based options as one possible tool.</li>
                  <li>You prefer a one-size-fits-all mood program.</li>
                </ul>
              </div>
            </div>
          </div>
        </section>


        {/* CTA Section */}
        <section className="pcos-section pcos-cta-section" id="book">
          <div className="pcos-container">
            <div className="pcos-cta-wrap">
              <div className="pcos-cta-content">
                <span className="pcos-eyebrow">Take The First Step</span>
                <h2>Stop losing two weeks of every month</h2>
                <ul className="pcos-check-list">
                  <li>
                    <CheckCircle size={18} />
                    <span>Finally get a clinician who knows the difference between PMS and PMDD</span>
                  </li>
                  <li>
                    <CheckCircle size={18} />
                    <span>Get a treatment plan built around YOUR cycle, not a one-size script</span>
                  </li>
                  <li>
                    <CheckCircle size={18} />
                    <span>Stop dreading your luteal phase and start trusting your body again</span>
                  </li>
                  <li>
                    <CheckCircle size={18} />
                    <span>Repair what PMDD has been quietly costing your relationships and career</span>
                  </li>
                </ul>
                <button className="pcos-btn pcos-btn-primary pcos-btn-large" onClick={scrollToBooking} data-testid="pmdd-mid-cta">
                  Book Your Free Consultation
                  <ArrowRight size={18} />
                </button>
                <p className="pcos-cta-note">Free & confidential. Limited spots available each week.</p>
              </div>
              <div className="pcos-cta-visual">
                <div className="pcos-guarantee-card">
                  <h3>Our Promise</h3>
                  <p>If you do not feel meaningfully better within 60 days of starting your plan, we will keep working with you for free until you do. No questions asked.</p>
                </div>
                <div className="pcos-testimonial-card">
                  <p>"For the first time in fifteen years, I had a luteal week where I did not lose myself. I cried, but this time it was relief."</p>
                  <span>PMDD patient, age 38</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="pcos-section">
          <div className="pcos-container">
            <div className="pcos-section-head">
              <span className="pcos-eyebrow">Questions</span>
              <h2>What you are probably wondering</h2>
            </div>
            <div className="pcos-faq-list">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className={`pcos-faq-item ${openFaq === index ? 'open' : ''}`}
                  data-testid={`pmdd-faq-item-${index}`}
                >
                  <button
                    className="pcos-faq-question"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    data-testid={`pmdd-faq-toggle-${index}`}
                  >
                    <span>{faq.question}</span>
                    {openFaq === index ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </button>
                  {openFaq === index && (
                    <div className="pcos-faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="pcos-section pcos-final-cta">
          <div className="pcos-container">
            <div className="pcos-final-cta-content">
              <h2>You have lost enough months</h2>
              <p>
                Every cycle you wait is another two weeks of damage you cannot get back. Another fight you wish you could rewind. Another version of yourself you do not recognize. You do not have to live like this anymore.
              </p>
              <button className="pcos-btn pcos-btn-primary pcos-btn-large" onClick={scrollToBooking} data-testid="pmdd-final-cta">
                Book Your Free Consultation Now
                <ArrowRight size={18} />
              </button>
              <p className="pcos-final-note">15 minutes could change every cycle from here.</p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="pcos-footer">
        <div className="pcos-container pcos-footer-inner">
          <span>© {new Date().getFullYear()} Glow Health LLC</span>
          <div className="pcos-footer-links">
            <a href="/privacy-policy">Privacy Policy</a>
            <span>·</span>
            <a href="/terms-of-service">Terms of Service</a>
          </div>
        </div>
      </footer>
      <ConditionExitPopup condition="pmdd" />
    </div>
  );
};

export default PMDDLanding;
